import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import {
  getCustomCounters,
  createCustomCounter,
  updateCustomCounter,
  deleteCustomCounter,
  resetCounter,
  toggleCounterEnabled
} from '../services/api';
import {
  FaPlus, FaEdit, FaTrash, FaToggleOn, FaToggleOff,
  FaSave, FaTimes, FaUndo, FaPalette, FaEye, FaEyeSlash
} from 'react-icons/fa';
import '../styles/CustomCounters.css';

function CustomCounters({ user }) {
  const [counters, setCounters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCounter, setEditingCounter] = useState(null);
  const [message, setMessage] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    value: 0,
    embed_title: 'Counter',
    title_url: '',
    embed_description: '',
    embed_color: '#5865F2',
    increment_message: 'Counter increased!',
    decrement_message: 'Counter decreased!',
    view_message: 'Current Status',
    show_value: true,
    show_timestamp: true,
    footer_text: '',
    footer_icon_url: '',
    thumbnail_url: '',
    image_url: '',
    author_name: '',
    author_url: '',
    author_icon_url: '',
    enabled: true
  });

  useEffect(() => {
    fetchCounters();
  }, []);

  const fetchCounters = async () => {
    try {
      setLoading(true);
      const data = await getCustomCounters();
      setCounters(data);
    } catch (error) {
      console.error('Error fetching counters:', error);
      setMessage('Failed to load counters ❌');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      display_name: '',
      value: 0,
      embed_title: 'Counter',
      title_url: '',
      embed_description: '',
      embed_color: '#5865F2',
      increment_message: 'Counter increased!',
      decrement_message: 'Counter decreased!',
      view_message: 'Current Status',
      show_value: true,
      show_timestamp: true,
      footer_text: '',
      footer_icon_url: '',
      thumbnail_url: '',
      image_url: '',
      author_name: '',
      author_url: '',
      author_icon_url: '',
      enabled: true
    });
    setEditingCounter(null);
    setShowForm(false);
  };

  const handleCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEdit = (counter) => {
    setFormData({
      name: counter.name,
      display_name: counter.display_name,
      value: counter.value,
      embed_title: counter.embed_title,
      title_url: counter.title_url || '',
      embed_description: counter.embed_description || '',
      embed_color: counter.embed_color,
      increment_message: counter.increment_message,
      decrement_message: counter.decrement_message,
      view_message: counter.view_message || 'Current Status',
      show_value: counter.show_value,
      show_timestamp: counter.show_timestamp !== undefined ? counter.show_timestamp : true,
      footer_text: counter.footer_text || '',
      footer_icon_url: counter.footer_icon_url || '',
      thumbnail_url: counter.thumbnail_url || '',
      image_url: counter.image_url || '',
      author_name: counter.author_name || '',
      author_url: counter.author_url || '',
      author_icon_url: counter.author_icon_url || '',
      enabled: counter.enabled
    });
    setEditingCounter(counter);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user.isAdmin) {
      setMessage('You need admin permissions ❌');
      return;
    }

    if (!formData.name || !formData.display_name) {
      setMessage('Name and Display Name are required ❌');
      return;
    }

    try {
      if (editingCounter) {
        await updateCustomCounter(editingCounter.id, formData);
        setMessage('Counter updated successfully! ✅ Commands refreshed automatically');
      } else {
        await createCustomCounter(formData);
        setMessage('Counter created successfully! ✅ Commands are now available in Discord');
      }

      resetForm();
      fetchCounters();
      setTimeout(() => setMessage(''), 4000);
    } catch (error) {
      console.error('Error saving counter:', error);
      setMessage(error.response?.data?.error || 'Failed to save counter ❌');
    }
  };

  const handleDelete = async (id) => {
    if (!user.isAdmin) return;

    if (!window.confirm('Are you sure you want to delete this counter?')) return;

    try {
      await deleteCustomCounter(id);
      setMessage('Counter deleted successfully! ✅ (Restart bot to remove commands)');
      fetchCounters();
      setTimeout(() => setMessage(''), 4000);
    } catch (error) {
      console.error('Error deleting counter:', error);
      setMessage('Failed to delete counter ❌');
    }
  };

  const handleToggle = async (id) => {
    if (!user.isAdmin) return;

    try {
      await toggleCounterEnabled(id);
      fetchCounters();
    } catch (error) {
      console.error('Error toggling counter:', error);
      setMessage('Failed to toggle counter ❌');
    }
  };

  const handleReset = async (id) => {
    if (!user.isAdmin) return;

    if (!window.confirm('Are you sure you want to reset this counter to 0?')) return;

    try {
      await resetCounter(id);
      setMessage('Counter reset successfully! ✅');
      fetchCounters();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error resetting counter:', error);
      setMessage('Failed to reset counter ❌');
    }
  };

  if (loading) {
    return (
      <div>
        <Navbar user={user} />
        <div className="loading-container">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <div>
            <h1>Custom Counter Commands</h1>
            <p>Create and manage custom counter commands: /nova add (increment), /nova sub (decrement), /nova view (status)</p>
          </div>
          {user.isAdmin && (
            <button onClick={handleCreate} className="create-btn">
              <FaPlus /> Create Counter
            </button>
          )}
        </div>

        {message && (
          <div className={`alert ${message.includes('✅') ? 'alert-success' : 'alert-error'}`}>
            {message}
          </div>
        )}

        {!user.isAdmin && (
          <div className="alert alert-info">
            🔒 You need admin permissions to create or modify counters
          </div>
        )}

        {showForm && (
          <div className="counter-form-overlay">
            <div className="counter-form-modal">
              <div className="form-modal-header">
                <h2>{editingCounter ? 'Edit Counter' : 'Create New Counter'}</h2>
                <button onClick={resetForm} className="close-btn">
                  <FaTimes />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="counter-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Command Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      placeholder="nova"
                      pattern="[a-z0-9-]+"
                      required
                      disabled={!!editingCounter}
                    />
                    <small>Lowercase letters, numbers, and hyphens only. Creates /name add, /name sub, and /name view</small>
                  </div>

                  <div className="form-group">
                    <label>Display Name *</label>
                    <input
                      type="text"
                      value={formData.display_name}
                      onChange={(e) => handleChange('display_name', e.target.value)}
                      placeholder="Nova"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Initial Value</label>
                    <input
                      type="number"
                      value={formData.value}
                      onChange={(e) => handleChange('value', parseInt(e.target.value) || 0)}
                      placeholder="0"
                    />
                  </div>

                  <div className="form-group color-picker-group">
                    <label>Embed Color</label>
                    <div className="color-input-wrapper">
                      <input
                        type="color"
                        value={formData.embed_color}
                        onChange={(e) => handleChange('embed_color', e.target.value)}
                      />
                      <input
                        type="text"
                        value={formData.embed_color}
                        onChange={(e) => handleChange('embed_color', e.target.value)}
                        placeholder="#5865F2"
                        className="color-hex"
                      />
                      <FaPalette className="color-icon" />
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Embed Title</label>
                    <input
                      type="text"
                      value={formData.embed_title}
                      onChange={(e) => handleChange('embed_title', e.target.value)}
                      placeholder="Counter"
                    />
                  </div>

                  <div className="form-group">
                    <label>Title URL (optional)</label>
                    <input
                      type="url"
                      value={formData.title_url}
                      onChange={(e) => handleChange('title_url', e.target.value)}
                      placeholder="https://example.com"
                    />
                    <small>Makes the title clickable</small>
                  </div>
                </div>

                <div className="form-group">
                  <label>Embed Description</label>
                  <input
                    type="text"
                    value={formData.embed_description}
                    onChange={(e) => handleChange('embed_description', e.target.value)}
                    placeholder="Optional description"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Increment Message (for /add)</label>
                    <input
                      type="text"
                      value={formData.increment_message}
                      onChange={(e) => handleChange('increment_message', e.target.value)}
                      placeholder="Counter increased!"
                    />
                  </div>

                  <div className="form-group">
                    <label>Decrement Message (for /sub)</label>
                    <input
                      type="text"
                      value={formData.decrement_message}
                      onChange={(e) => handleChange('decrement_message', e.target.value)}
                      placeholder="Counter decreased!"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>View Message (for /view)</label>
                  <input
                    type="text"
                    value={formData.view_message}
                    onChange={(e) => handleChange('view_message', e.target.value)}
                    placeholder="Current Status"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Footer Text</label>
                    <input
                      type="text"
                      value={formData.footer_text}
                      onChange={(e) => handleChange('footer_text', e.target.value)}
                      placeholder="Optional footer"
                    />
                  </div>

                  <div className="form-group">
                    <label>Footer Icon URL (optional)</label>
                    <input
                      type="url"
                      value={formData.footer_icon_url}
                      onChange={(e) => handleChange('footer_icon_url', e.target.value)}
                      placeholder="https://example.com/icon.png"
                    />
                    <small>Small icon next to footer text</small>
                  </div>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.show_timestamp}
                      onChange={(e) => handleChange('show_timestamp', e.target.checked)}
                    />
                    Show Timestamp
                  </label>
                  <small>Display timestamp at the bottom of the embed</small>
                </div>

                <div className="form-section">
                  <h3>Advanced Embed Options</h3>
                  <p className="help-text">💡 Use <code>{`{value}`}</code> or <code>{`{count}`}</code> in any text field to show the current counter value</p>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Author Name</label>
                      <input
                        type="text"
                        value={formData.author_name}
                        onChange={(e) => handleChange('author_name', e.target.value)}
                        placeholder="Optional author name"
                      />
                    </div>

                    <div className="form-group">
                      <label>Author URL (optional)</label>
                      <input
                        type="url"
                        value={formData.author_url}
                        onChange={(e) => handleChange('author_url', e.target.value)}
                        placeholder="https://example.com"
                      />
                      <small>Makes author name clickable</small>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Author Icon URL</label>
                    <input
                      type="url"
                      value={formData.author_icon_url}
                      onChange={(e) => handleChange('author_icon_url', e.target.value)}
                      placeholder="https://example.com/icon.png"
                    />
                    <small>Small icon next to author name</small>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Thumbnail URL</label>
                      <input
                        type="text"
                        value={formData.thumbnail_url}
                        onChange={(e) => handleChange('thumbnail_url', e.target.value)}
                        placeholder="https://..."
                      />
                    </div>

                    <div className="form-group">
                      <label>Image URL</label>
                      <input
                        type="text"
                        value={formData.image_url}
                        onChange={(e) => handleChange('image_url', e.target.value)}
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                </div>

                <div className="form-toggles">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={formData.show_value}
                      onChange={(e) => handleChange('show_value', e.target.checked)}
                    />
                    <span className="toggle-text">
                      {formData.show_value ? <FaEye /> : <FaEyeSlash />}
                      Show Counter Value
                    </span>
                  </label>

                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={formData.enabled}
                      onChange={(e) => handleChange('enabled', e.target.checked)}
                    />
                    <span className="toggle-text">
                      {formData.enabled ? <FaToggleOn /> : <FaToggleOff />}
                      Enabled
                    </span>
                  </label>
                </div>

                <div className="form-actions">
                  <button type="submit" className="save-btn">
                    <FaSave /> {editingCounter ? 'Update Counter' : 'Create Counter'}
                  </button>
                  <button type="button" onClick={resetForm} className="cancel-btn">
                    <FaTimes /> Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="counters-grid">
          {counters.length === 0 ? (
            <div className="empty-state">
              <h3>No custom counters yet</h3>
              <p>Create your first counter to get started!</p>
            </div>
          ) : (
            counters.map((counter) => (
              <div key={counter.id} className="counter-card">
                <div className="counter-header">
                  <div className="counter-title">
                    <h3>/{counter.name}</h3>
                    <span className={`status-badge ${counter.enabled ? 'enabled' : 'disabled'}`}>
                      {counter.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  {user.isAdmin && (
                    <button
                      onClick={() => handleToggle(counter.id)}
                      className={`toggle-btn ${counter.enabled ? 'enabled' : 'disabled'}`}
                      title={counter.enabled ? 'Disable' : 'Enable'}
                    >
                      {counter.enabled ? <FaToggleOn /> : <FaToggleOff />}
                    </button>
                  )}
                </div>

                <div className="counter-body">
                  <div className="counter-info">
                    <p><strong>Display Name:</strong> {counter.display_name}</p>
                    <p><strong>Current Value:</strong> {counter.value}</p>
                    <p><strong>Commands:</strong> /{counter.name} add, /{counter.name} sub, /{counter.name} view</p>
                  </div>

                  <div className="counter-preview" style={{ borderLeftColor: counter.embed_color }}>
                    <h4 style={{ color: counter.embed_color }}>{counter.embed_title}</h4>
                    {counter.embed_description && <p className="preview-desc">{counter.embed_description}</p>}
                    <div className="preview-field">
                      <strong>{counter.increment_message}</strong>
                      <p>{counter.show_value ? `Aktueller Wert: ${counter.value}` : ''}</p>
                    </div>
                  </div>
                </div>

                {user.isAdmin && (
                  <div className="counter-actions">
                    <button onClick={() => handleEdit(counter)} className="action-btn edit">
                      <FaEdit /> Edit
                    </button>
                    <button onClick={() => handleReset(counter.id)} className="action-btn reset">
                      <FaUndo /> Reset
                    </button>
                    <button onClick={() => handleDelete(counter.id)} className="action-btn delete">
                      <FaTrash /> Delete
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
}

export default CustomCounters;
